import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { adminApi } from "@/lib/admin";
import { ArrowLeft, Loader2 } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";
import EngineHealthSection from "@/components/EngineHealthSection";
import FailoverLogSection from "@/components/FailoverLogSection";
import UserAnalyticsSection from "@/components/UserAnalyticsSection";
import HBMEvalSection from "@/components/HBMEvalSection";
import AdminApiKeysSection from "@/components/AdminApiKeysSection";
import AdminApiAllowlistSection from "@/components/AdminApiAllowlistSection";
import SuperAdminAllowlistSection from "@/components/SuperAdminAllowlistSection";
import ResearchInvitesSection from "@/components/ResearchInvitesSection";
import CurationSection from "@/components/CurationSection";
import AdminCorpusIngestSection from "@/components/AdminCorpusIngestSection";
import AdminCorpusJsonlUrlSection from "@/components/AdminCorpusJsonlUrlSection";
import AdminCorpusJsonlFileSection from "@/components/AdminCorpusJsonlFileSection";
import AdminTokenUsageSection from "@/components/AdminTokenUsageSection";
import AdminModelTokenUsageSection from "@/components/AdminModelTokenUsageSection";
import AdminDynamicHBMSection from "@/components/AdminDynamicHBMSection";
import AdminTrainingCorpusSection from "@/components/AdminTrainingCorpusSection";
import RealtimeUsageSection from "@/components/RealtimeUsageSection";
import RuntimeEngineSetupSection from "@/components/RuntimeEngineSetupSection";

const TOKEN_LIMIT_PRESETS = [
  { id: "2m", label: "2M", monthly_token_cap: 2_000_000, daily_message_cap: 100, monthly_message_cap: 1_000 },
  { id: "5m", label: "5M", monthly_token_cap: 5_000_000, daily_message_cap: 250, monthly_message_cap: 2_500 },
  { id: "10m", label: "10M", monthly_token_cap: 10_000_000, daily_message_cap: 500, monthly_message_cap: 5_000 },
];

const MAIN_ENGINE_CARDS = [
  { key: "nscale_qwen", label: "Nscale", fallback: "1" },
  { key: "avian_deepseek", label: "Avian", fallback: "2" },
  { key: "gemini", label: "Gemini", fallback: "3" },
  { key: "openai", label: "OpenAI", fallback: "4" },
  { key: "claude", label: "Claude", fallback: "5", finalOnly: true },
];

const fmtLimit = (v) => (typeof v === "number" ? v.toLocaleString() : "default");

// ─── Layout primitives ────────────────────────────────────────────────

function Card({ title, description, children, testid, action }) {
  return (
    <section
      data-testid={testid}
      className="border border-[var(--border)] p-6 md:p-7 mb-5"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="min-w-0">
          <h3 className="font-mono-ui text-[11px] tracking-[0.34em] uppercase text-[var(--text)]">
            {title}
          </h3>
          {description && (
            <p className="font-sans text-sm text-[var(--text-muted)] leading-relaxed mt-2 max-w-3xl">
              {description}
            </p>
          )}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function KpiCell({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="px-4 py-3 border-r border-[var(--border)] last:border-r-0 min-w-0"
    >
      <div className="font-mono-ui text-[9px] tracking-[0.26em] uppercase text-[var(--text-dim)] truncate">
        {label}
      </div>
      <div className="font-serif text-2xl md:text-3xl text-[var(--text)] mt-1 truncate">
        {value ?? "—"}
      </div>
    </div>
  );
}

function TabButton({ id, label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-3 font-mono-ui text-[10px] tracking-[0.28em] uppercase border-b-2 transition-colors whitespace-nowrap ${
        active
          ? "border-[var(--accent)] text-[var(--accent)]"
          : "border-transparent text-[var(--text-muted)] hover:text-[var(--text)]"
      }`}
      aria-selected={active}
      role="tab"
      id={`tab-${id}`}
    >
      {label}
    </button>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────

const TABS = [
  { id: "users", label: "Users & Access" },
  { id: "voice", label: "Voice & Realtime" },
  { id: "corpus", label: "Corpus & Curation" },
  { id: "training_corpus", label: "Training Corpus" },
  { id: "engines", label: "Engines & Evals" },
  { id: "usage", label: "Usage Analytics" },
  { id: "model_tokens", label: "Model Tokens" },
  { id: "extended_thinking", label: "Extended Thinking" },
];

export default function Admin() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = useMemo(() => {
    const t = searchParams.get("tab");
    return TABS.some((x) => x.id === t) ? t : "users";
  }, [searchParams]);

  const [engines, setEngines] = useState(null);
  const [switching, setSwitching] = useState(false);

  const [evalQuery, setEvalQuery] = useState("");
  const [evalResults, setEvalResults] = useState(null);
  const [evalLoading, setEvalLoading] = useState(false);
  const [evalError, setEvalError] = useState("");

  const [memEvalQuery, setMemEvalQuery] = useState("");
  const [memEvalResults, setMemEvalResults] = useState(null);
  const [memEvalLoading, setMemEvalLoading] = useState(false);
  const [memEvalError, setMemEvalError] = useState("");
  const [memEvalVoted, setMemEvalVoted] = useState(null);

  const [stats, setStats] = useState(null);
  const [audit, setAudit] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersBusyId, setUsersBusyId] = useState(null);

  const [createForm, setCreateForm] = useState({ email: "", password: "", role: "user" });
  const [createBusy, setCreateBusy] = useState(false);
  const [createMsg, setCreateMsg] = useState("");

  const [pwdForm, setPwdForm] = useState({ current_password: "", new_password: "" });
  const [pwdBusy, setPwdBusy] = useState(false);
  const [pwdMsg, setPwdMsg] = useState("");

  const refreshUsers = () =>
    adminApi
      .dashboardUsers({ page: 1, page_size: 200, include_deleted: true })
      .then((res) => setUsers(res.users || []))
      .catch(() => {});

  // Global bootstrap: stats always (KPI strip). Everything else — per active tab.
  useEffect(() => {
    if (user && user.role === "admin") {
      adminApi.stats().then(setStats).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (!(user && user.role === "admin")) return;
    if (activeTab === "users") {
      refreshUsers();
    } else if (activeTab === "engines") {
      adminApi.engines().then(setEngines).catch(() => {});
    } else if (activeTab === "usage") {
      adminApi.audit(50).then(setAudit).catch(() => {});
    }
    // voice, corpus tabs render self-fetching child sections
  }, [activeTab, user]);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] text-[var(--text-muted)] font-mono-ui text-[11px] tracking-[0.3em] uppercase">
        Loading…
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" state={{ from: "/admin" }} replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const setTab = (id) => setSearchParams({ tab: id }, { replace: true });

  const switchEngine = async (target) => {
    if (switching) return;
    setSwitching(true);
    try {
      await adminApi.setEngine(target);
      const e = await adminApi.engines();
      setEngines(e);
    } catch (err) {
      alert(err.message || "Failed to switch engine.");
    } finally {
      setSwitching(false);
    }
  };

  const runEval = async (e) => {
    e.preventDefault();
    if (!evalQuery.trim() || evalLoading) return;
    setEvalLoading(true);
    setEvalError("");
    setEvalResults(null);
    try {
      const r = await adminApi.eval(evalQuery.trim());
      setEvalResults(r);
    } catch (err) {
      setEvalError(err.message || "Eval failed.");
    } finally {
      setEvalLoading(false);
    }
  };

  const runMemoryEval = async (e) => {
    e.preventDefault();
    if (!memEvalQuery.trim() || memEvalLoading) return;
    setMemEvalLoading(true);
    setMemEvalError("");
    setMemEvalResults(null);
    setMemEvalVoted(null);
    try {
      const r = await adminApi.memoryEval(memEvalQuery.trim());
      setMemEvalResults(r);
    } catch (err) {
      setMemEvalError(err.message || "Memory eval failed.");
    } finally {
      setMemEvalLoading(false);
    }
  };

  const voteMemoryEval = async (winner) => {
    if (!memEvalResults || memEvalVoted) return;
    try {
      await adminApi.memoryEvalVote(memEvalResults.query, winner, "");
      setMemEvalVoted(winner);
    } catch (err) {
      alert(err.message || "Vote failed.");
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <header className="border-b border-[var(--border)] px-6 md:px-10 py-5 flex items-center justify-between">
        <Link
          to="/"
          data-testid="admin-back"
          className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[var(--text-muted)] hover:text-[var(--text)] inline-flex items-center gap-2 transition-colors"
        >
          <ArrowLeft size={13} /> Back to chat
        </Link>
        <h1 className="font-mono-ui text-[11px] tracking-[0.4em] uppercase">Admin</h1>
        <div className="flex items-center gap-5">
          <Link
            to="/admin/agents"
            data-testid="admin-to-agents"
            className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--accent)] hover:underline"
          >
            AI agents
          </Link>
          <Link
            to="/investors"
            data-testid="admin-to-investors"
            className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--accent)] hover:underline"
          >
            Investor brief
          </Link>
          <Link
            to="/metrics"
            data-testid="admin-to-metrics"
            className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--accent)] hover:underline"
          >
            Press metrics
          </Link>
          <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-dim)] hidden md:inline">
            {user.email}
          </span>
        </div>
      </header>

      {/* KPI Strip — sticky, always visible */}
      <div
        data-testid="kpi-strip"
        className="sticky top-0 z-20 bg-[var(--bg)] border-b border-[var(--border)]"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-3 md:grid-cols-6 border-l border-r border-[var(--border)]">
            <KpiCell testid="kpi-users-total" label="Users total" value={stats?.users?.total} />
            <KpiCell testid="kpi-joined-24h" label="Joined 24h" value={stats?.users?.joined_24h} />
            <KpiCell testid="kpi-chats-24h" label="Chats 24h" value={stats?.chats?.in_24h} />
            <KpiCell testid="kpi-chats-7d" label="Chats 7d" value={stats?.chats?.in_7d} />
            <KpiCell testid="kpi-memories" label="Memories" value={stats?.memories?.total} />
            <KpiCell testid="kpi-anon" label="Anon visitors" value={stats?.anonymous?.distinct_visitors} />
          </div>
        </div>

        {/* Tab bar */}
        <div className="max-w-6xl mx-auto px-6 md:px-10 border-t border-[var(--border)]">
          <div role="tablist" className="flex gap-1 overflow-x-auto -mb-px">
            {TABS.map((t) => (
              <TabButton
                key={t.id}
                id={t.id}
                label={t.label}
                active={activeTab === t.id}
                onClick={() => setTab(t.id)}
                testid={`tab-btn-${t.id}`}
              />
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-8 pb-20">
        {activeTab === "users" && (
          <div data-testid="tab-panel-users" role="tabpanel" aria-labelledby="tab-users">
            <UsersTab
              users={users}
              currentUser={user}
              usersBusyId={usersBusyId}
              setUsersBusyId={setUsersBusyId}
              refreshUsers={refreshUsers}
              createForm={createForm}
              setCreateForm={setCreateForm}
              createBusy={createBusy}
              setCreateBusy={setCreateBusy}
              createMsg={createMsg}
              setCreateMsg={setCreateMsg}
              pwdForm={pwdForm}
              setPwdForm={setPwdForm}
              pwdBusy={pwdBusy}
              setPwdBusy={setPwdBusy}
              pwdMsg={pwdMsg}
              setPwdMsg={setPwdMsg}
            />
          </div>
        )}

        {activeTab === "voice" && (
          <div data-testid="tab-panel-voice" role="tabpanel" aria-labelledby="tab-voice">
            <Card
              title="Realtime voice · cost"
              testid="section-realtime-usage"
              description="Live cost tracker for Direct Voice (OpenAI Realtime). Minutes, spend, and per-user drill-down for the current month. Computed from token usage the browser reports at session-end — accurate down to the response-block. Regular users never see this section."
            >
              <RealtimeUsageSection />
            </Card>
          </div>
        )}

        {activeTab === "corpus" && (
          <div data-testid="tab-panel-corpus" role="tabpanel" aria-labelledby="tab-corpus">
            <Card
              title="Bulk-load 10K gold persona Q&A"
              testid="section-corpus-ingest"
              description="One-shot ingest for the bundled persona Q&A PDFs."
            >
              <AdminCorpusIngestSection />
            </Card>
            <Card
              title="Upload JSONL file (simple)"
              testid="section-corpus-jsonl-file"
              description="Pick a file → click Start → watch progress. The browser streams the JSONL in 250-row chunks to the backend. No URL fetch, no background job, no silent black box."
            >
              <AdminCorpusJsonlFileSection />
            </Card>
            <Card
              title="Ingest JSONL from URL (advanced)"
              testid="section-corpus-jsonl-url"
              description="One-click JSONL URL ingest. Fallback for when the file lives on a remote artifact bucket. May fail on pods with blocked egress — prefer the file uploader above."
            >
              <AdminCorpusJsonlUrlSection />
            </Card>
            <Card
              title="Curation — great replies to corpus"
              testid="section-curation"
              description="Promote real chat exchanges into the gold corpus."
            >
              <CurationSection />
            </Card>
          </div>
        )}

        {activeTab === "engines" && (
          <div data-testid="tab-panel-engines" role="tabpanel" aria-labelledby="tab-engines">
            <Card
              title="Engines"
              testid="section-engines"
              description="Main chat uses only the approved paid/provider ladder: Nscale → Avian → Gemini → OpenAI → Claude. Backup AI engines below are stored separately and do not appear in main traffic or evals."
            >
              {engines ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {MAIN_ENGINE_CARDS.map(({ key: name, label, fallback, finalOnly }) => {
                    const eng = engines[name];
                    const active = engines.active === name;
                    return (
                      <div
                        key={name}
                        data-testid={`engine-card-${name}`}
                        className={`border px-5 py-5 ${
                          active ? "border-[var(--accent)]" : "border-[var(--border)]"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono-ui text-[10px] tracking-[0.28em] uppercase">
                            {fallback}. {label}
                          </span>
                          {active && (
                            <span
                              data-testid={`engine-active-${name}`}
                              className="font-mono-ui text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 border border-[var(--accent)] text-[var(--accent)]"
                            >
                              Active
                            </span>
                          )}
                        </div>
                        <div className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text-dim)] mb-4">
                          {finalOnly ? `${eng?.model || "Claude"} · final fallback only` : (eng?.model || "not configured")}
                        </div>
                        {!active && !finalOnly && (
                          <button
                            onClick={() => switchEngine(name)}
                            disabled={switching || !eng?.configured}
                            data-testid={`switch-to-${name}`}
                            className="w-full py-2 font-mono-ui text-[10px] tracking-[0.28em] uppercase border border-[var(--text-muted)] hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            {switching ? "Switching…" : eng?.configured ? "Make active" : "Not configured"}
                          </button>
                        )}
                        {finalOnly && (
                          <div
                            data-testid="engine-final-fallback-claude"
                            className="w-full py-2 font-mono-ui text-[10px] tracking-[0.24em] uppercase border border-[var(--border)] text-[var(--text-muted)] text-center"
                          >
                            Final backup
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="font-sans text-sm text-[var(--text-dim)]">Loading engines…</p>
              )}
            </Card>

            <Card
              title="Backup AI Engines"
              testid="section-runtime-engine-setup"
              description="Store non-primary or free/runtime engines here only. These backups are not shown in Main Engine cards and are not used by evals or live chat fallback."
            >
              <RuntimeEngineSetupSection onSaved={() => adminApi.engines().then(setEngines).catch(() => {})} />
            </Card>

            <Card
              title="Engine health"
              testid="section-engine-health"
              description="Live success / retry / fallback breakdown for the chat pipeline. The active engine is tried first; failures walk Nscale → Avian → Gemini → OpenAI → Claude."
            >
              <EngineHealthSection />
            </Card>

            <Card
              title="Failover log"
              testid="section-failover-log"
              description="Every time the approved ladder walks — Nscale → Avian → Gemini → OpenAI → Claude — the hop is logged here with the fail reason and timestamp. Rows expire after 7 days."
            >
              <FailoverLogSection />
            </Card>

            <Card
              title="HBM Consult"
              testid="section-hbm-consult"
              description="A rigorous strategic memo engine. Ask a plain-English question, paste supporting data, and receive an audited memo — every claim sourced, every framework selected on merit, every recommendation stated with its own counter-argument."
              action={
                <Link
                  to="/admin/hbm-consult"
                  data-testid="hbm-consult-open"
                  className="px-6 py-2.5 bg-[var(--accent)] text-[var(--bg)] hover:opacity-90 font-mono-ui text-[10px] tracking-[0.28em] uppercase transition-opacity"
                >
                  Open HBM Consult
                </Link>
              }
            >
              <p className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
                Admin-only · Same rigor online or offline
              </p>
            </Card>

            <Card
              title="HBM evaluation harness"
              testid="section-hbm-eval"
              description="Single question, three branches: Gemini under the HBM system prompt, Claude under the same HBM system prompt, and a Generic Baseline (no HBM). Identical user message, no memory, no corpus — only the reasoning layer differs."
            >
              <HBMEvalSection />
            </Card>

            <Card
              title="Side-by-side eval"
              testid="section-eval"
              description="Sends the same query through the approved ladder only: Nscale, Avian, Gemini, OpenAI, and Claude. Backup/runtime engines are not included."
            >
              <form onSubmit={runEval} className="space-y-3">
                <textarea
                  value={evalQuery}
                  onChange={(e) => setEvalQuery(e.target.value)}
                  placeholder="e.g. My co-founder wants to pivot and we are 50-50 on equity. What should I do?"
                  data-testid="eval-input"
                  rows={3}
                  className="w-full bg-transparent border border-[var(--border)] focus:border-[var(--accent)] outline-none p-3 font-sans text-sm resize-none transition-colors"
                />
                <button
                  type="submit"
                  disabled={evalLoading || !evalQuery.trim()}
                  data-testid="eval-submit"
                  className="px-5 py-2 font-mono-ui text-[10px] tracking-[0.28em] uppercase border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] disabled:opacity-40 transition-colors inline-flex items-center gap-2"
                >
                  {evalLoading && <Loader2 size={12} className="animate-spin" />}
                  {evalLoading ? "Running…" : "Run approved ladder"}
                </button>
              </form>
              {evalError && (
                <p className="font-sans text-sm text-red-400 border-l-2 border-red-400 pl-3 mt-4" data-testid="eval-error">
                  {evalError}
                </p>
              )}
              {evalResults && (
                <div className="grid md:grid-cols-2 gap-4 mt-6">
                  {evalResults.results.map((r) => (
                    <div key={r.engine} data-testid={`eval-result-${r.engine}`} className="border border-[var(--border)] p-5">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-mono-ui text-[10px] tracking-[0.28em] uppercase">{r.engine}</span>
                        <span className="font-mono-ui text-[9px] tracking-[0.18em] uppercase text-[var(--text-dim)]">
                          {r.model} · {r.latency_ms}ms
                        </span>
                      </div>
                      {r.error ? (
                        <p className="font-mono-ui text-xs text-red-400 whitespace-pre-wrap">{r.error}</p>
                      ) : (
                        <p className="font-sans text-sm leading-relaxed whitespace-pre-wrap">{r.content}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card
              title="Memory eval (with vs without)"
              testid="section-memory-eval"
              description="Runs the same query through the active engine twice — once with your stored memory injected, once without. Vote which one is sharper to calibrate retrieval over time."
            >
              <form onSubmit={runMemoryEval} className="space-y-3">
                <textarea
                  value={memEvalQuery}
                  onChange={(e) => setMemEvalQuery(e.target.value)}
                  placeholder="e.g. What should I focus on this quarter given everything you know about me?"
                  data-testid="memory-eval-input"
                  rows={3}
                  className="w-full bg-transparent border border-[var(--border)] focus:border-[var(--accent)] outline-none p-3 font-sans text-sm resize-none transition-colors"
                />
                <button
                  type="submit"
                  disabled={memEvalLoading || !memEvalQuery.trim()}
                  data-testid="memory-eval-submit"
                  className="px-5 py-2 font-mono-ui text-[10px] tracking-[0.28em] uppercase border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] disabled:opacity-40 transition-colors inline-flex items-center gap-2"
                >
                  {memEvalLoading && <Loader2 size={12} className="animate-spin" />}
                  {memEvalLoading ? "Running…" : "Run with vs without memory"}
                </button>
              </form>
              {memEvalError && (
                <p className="font-sans text-sm text-red-400 border-l-2 border-red-400 pl-3 mt-4" data-testid="memory-eval-error">
                  {memEvalError}
                </p>
              )}
              {memEvalResults && (
                <div className="mt-6 space-y-4">
                  <div className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
                    Engine: {memEvalResults.engine} · {memEvalResults.model} · {memEvalResults.memory_used_count} memories injected
                  </div>
                  {memEvalResults.memory_used_count > 0 && (
                    <details className="border border-[var(--border)] p-3" data-testid="memory-eval-context">
                      <summary className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] cursor-pointer">
                        Memories used ({memEvalResults.memory_used_count})
                      </summary>
                      <ul className="mt-3 space-y-1 text-sm font-sans text-[var(--text-muted)]">
                        {memEvalResults.memory_used_preview.map((m, i) => (
                          <li key={i}>
                            <span className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)] mr-2">
                              {m.type}
                            </span>
                            {m.content}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div data-testid="memory-eval-with" className="border border-[var(--border)] p-5">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--accent)]">With memory</span>
                        <span className="font-mono-ui text-[9px] tracking-[0.18em] uppercase text-[var(--text-dim)]">
                          {memEvalResults.with_memory.latency_ms}ms
                        </span>
                      </div>
                      {memEvalResults.with_memory.error ? (
                        <p className="font-mono-ui text-xs text-red-400 whitespace-pre-wrap">{memEvalResults.with_memory.error}</p>
                      ) : (
                        <p className="font-sans text-sm leading-relaxed whitespace-pre-wrap">{memEvalResults.with_memory.content}</p>
                      )}
                    </div>
                    <div data-testid="memory-eval-without" className="border border-[var(--border)] p-5">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text-muted)]">Without memory</span>
                        <span className="font-mono-ui text-[9px] tracking-[0.18em] uppercase text-[var(--text-dim)]">
                          {memEvalResults.without_memory.latency_ms}ms
                        </span>
                      </div>
                      {memEvalResults.without_memory.error ? (
                        <p className="font-mono-ui text-xs text-red-400 whitespace-pre-wrap">{memEvalResults.without_memory.error}</p>
                      ) : (
                        <p className="font-sans text-sm leading-relaxed whitespace-pre-wrap">{memEvalResults.without_memory.content}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 items-center" data-testid="memory-eval-vote-row">
                    <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] mr-2">
                      Which is sharper?
                    </span>
                    {["with_memory", "without_memory", "tie"].map((w) => (
                      <button
                        key={w}
                        onClick={() => voteMemoryEval(w)}
                        disabled={!!memEvalVoted}
                        data-testid={`memory-eval-vote-${w}`}
                        className={`px-3 py-1.5 font-mono-ui text-[10px] tracking-[0.24em] uppercase border transition-colors ${
                          memEvalVoted === w
                            ? "border-[var(--accent)] text-[var(--accent)] bg-[var(--accent)]/10"
                            : "border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
                        } disabled:opacity-60`}
                      >
                        {w === "with_memory" ? "With" : w === "without_memory" ? "Without" : "Tie"}
                      </button>
                    ))}
                    {memEvalVoted && (
                      <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)] ml-2">
                        Logged.
                      </span>
                    )}
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}

        {activeTab === "training_corpus" && (
          <div data-testid="tab-panel-training-corpus" role="tabpanel" aria-labelledby="tab-training_corpus">
            <Card
              title="Training Corpus"
              testid="section-training-corpus"
              description="Consent-gated user conversations and admin training conversations. Memory-on is not enough; regular-user rows appear only after explicit training-data opt-in."
            >
              <AdminTrainingCorpusSection />
            </Card>
          </div>
        )}

        {activeTab === "usage" && (
          <div data-testid="tab-panel-usage" role="tabpanel" aria-labelledby="tab-usage">
            <Card
              title="User analytics"
              testid="section-user-analytics"
              description="Real-time behavior roll-up for registered users. Shows who's active today / this week, who's at their daily-query cap (10/day), and who's approaching the monthly token cap (1M). Token counts are admin-only."
              action={
                <Link
                  to="/admin/analytics"
                  data-testid="open-full-analytics"
                  className="px-5 py-2 bg-[var(--accent)] text-[var(--bg)] hover:opacity-90 font-mono-ui text-[10px] tracking-[0.28em] uppercase transition-opacity whitespace-nowrap"
                >
                  Open Full Analytics
                </Link>
              }
            >
              <UserAnalyticsSection />
            </Card>

            <Card
              title="Token usage — all users"
              testid="section-token-usage"
              description="Cross-user token usage with filters, power-user badges, and CSV export."
            >
              <AdminTokenUsageSection />
            </Card>

            <Card
              title="API keys"
              testid="section-api-keys"
              description="Your own external API keys used by the platform."
            >
              <AdminApiKeysSection />
            </Card>

            <Card
              title="Recent audit"
              testid="section-audit"
              description="Last 50 events across all users. Includes memory pipeline decisions and corpus retrieval. Anonymous visitors show as 'anonymous'."
            >
              <div className="space-y-2 font-mono-ui text-[11px]">
                {audit.length === 0 && <p className="text-[var(--text-dim)]">No events yet.</p>}
                {audit.map((a) => (
                  <div
                    key={a.id}
                    data-testid={`audit-row-${a.id}`}
                    className="border-l-2 border-[var(--border)] pl-3 py-2 hover:border-[var(--accent)] transition-colors"
                  >
                    <div className="flex items-center gap-3 text-[var(--text-dim)] tracking-[0.14em] uppercase text-[9px] flex-wrap">
                      <span>{a.created_at ? a.created_at.slice(0, 19).replace("T", " ") : ""}</span>
                      <span>·</span>
                      <span>{a.user_label}</span>
                      {a.classification?.domain && (
                        <>
                          <span>·</span>
                          <span>domain={a.classification.domain}</span>
                        </>
                      )}
                      {a.classification?.risk && (
                        <>
                          <span>·</span>
                          <span>risk={a.classification.risk}</span>
                        </>
                      )}
                      <span>·</span>
                      <span>mem={a.memory_used_count}</span>
                      <span>·</span>
                      <span>cases={a.cases_used_count}</span>
                      {a.hbm_activation && (
                        <>
                          <span>·</span>
                          <span>activation={a.hbm_activation}</span>
                        </>
                      )}
                    </div>
                    <div className="text-[var(--text)] text-xs mt-1 truncate">{a.query_preview}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {activeTab === "model_tokens" && (
          <div data-testid="tab-panel-model-tokens" role="tabpanel" aria-labelledby="tab-model_tokens">
            <Card
              title="Model token ledger"
              testid="section-model-token-usage"
              description="Daily, weekly, and monthly token counts for every model that actually served HBM chat traffic. Grouped by engine and provider model."
            >
              <AdminModelTokenUsageSection />
            </Card>
          </div>
        )}

        {activeTab === "extended_thinking" && (
          <div data-testid="tab-panel-extended-thinking" role="tabpanel" aria-labelledby="tab-extended_thinking">
            <Card
              title="Extended Thinking"
              testid="section-extended-thinking"
              description="Admin-only evaluation mode for deeper stateful reasoning. Compare fixed HBM against Extended Thinking before any broader rollout."
            >
              <AdminDynamicHBMSection />
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}

// ─── Users tab (contains most of the user management surface) ─────────

function UsersTab({
  users,
  currentUser,
  usersBusyId,
  setUsersBusyId,
  refreshUsers,
  createForm,
  setCreateForm,
  createBusy,
  setCreateBusy,
  createMsg,
  setCreateMsg,
  pwdForm,
  setPwdForm,
  pwdBusy,
  setPwdBusy,
  pwdMsg,
  setPwdMsg,
}) {
  const [detailsId, setDetailsId] = useState(null);
  const detailsUser = users.find((u) => u.id === detailsId) || null;

  return (
    <>
      <Card
        title="Users"
        testid="section-users"
        description="Registered accounts. Click a row to open the details drawer — full email, activity, and every per-user control (role, unlimited access, memory/voice betas, force-logout, delete) live inside."
      >
        <div className="space-y-1 mb-6">
          {users.map((u) => {
            const isSelf = currentUser && u.id === currentUser.id;
            const badges = [];
            if (u.role === "admin") badges.push({ k: "admin", cls: "text-[var(--accent)] border-[var(--accent)]" });
            if (u.limits?.unlimited) badges.push({ k: "unlimited", cls: "text-emerald-400 border-emerald-500/60" });
            if (u.tier && u.tier !== "free") badges.push({ k: u.tier, cls: "text-[var(--text-muted)] border-[var(--border)]" });
            if (u.deleted_at) badges.push({ k: "deleted", cls: "text-red-400 border-red-500/60" });
            if (u.memory_enabled === false) badges.push({ k: "memory off", cls: "text-[var(--text-dim)] border-[var(--border)]" });
            if (u.role !== "admin" && u.voice_beta) badges.push({ k: "voice", cls: "text-[var(--text-muted)] border-[var(--border)]" });
            if (u.role !== "admin" && u.direct_voice_beta) badges.push({ k: "direct voice", cls: "text-[var(--text-muted)] border-[var(--border)]" });
            if (isSelf) badges.push({ k: "you", cls: "text-[var(--text-dim)] border-[var(--border)]" });
            return (
              <button
                key={u.id}
                onClick={() => setDetailsId(u.id)}
                data-testid={`user-row-${u.id}`}
                className="w-full text-left border border-[var(--border)] px-4 py-3 hover:border-[var(--accent)] transition-colors flex items-center gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-ui text-[var(--text)] text-sm break-all">
                    {u.email}
                  </div>
                  <div className="font-mono-ui text-[9px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span>{u.created_at ? u.created_at.slice(0, 10) : ""}</span>
                    {typeof u.chat_count === "number" && <span>{u.chat_count} chats</span>}
                    {u.last_active && <span>seen {u.last_active.slice(0, 10)}</span>}
                    {badges.map((b) => (
                      <span
                        key={b.k}
                        className={`font-mono-ui text-[9px] tracking-[0.2em] uppercase border px-1.5 py-0.5 ${b.cls}`}
                      >
                        {b.k}
                      </span>
                    ))}
                  </div>
                </div>
                <span
                  data-testid={`user-details-${u.id}`}
                  className="font-mono-ui text-[10px] tracking-[0.28em] uppercase px-3 py-1.5 border border-[var(--border)] text-[var(--text-muted)]"
                >
                  Details →
                </span>
              </button>
            );
          })}
          {users.length === 0 && (
            <p className="font-sans text-sm text-[var(--text-dim)]">No users.</p>
          )}
        </div>

        <div className="border border-[var(--border)] p-5">
          <p className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text-muted)] mb-4">
            Create user
          </p>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (createBusy) return;
              setCreateBusy(true);
              setCreateMsg("");
              try {
                await adminApi.createUser({
                  email: createForm.email.trim().toLowerCase(),
                  password: createForm.password,
                  role: createForm.role,
                });
                setCreateMsg(`Created ${createForm.email} as ${createForm.role}.`);
                setCreateForm({ email: "", password: "", role: "user" });
                await refreshUsers();
              } catch (err) {
                setCreateMsg(err.message || "Failed.");
              } finally {
                setCreateBusy(false);
              }
            }}
            className="grid md:grid-cols-4 gap-3 items-end"
          >
            <input
              type="email"
              required
              placeholder="email"
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              data-testid="create-user-email"
              className="bg-transparent border-b border-[var(--border)] focus:border-[var(--accent)] outline-none py-2 text-sm transition-colors"
            />
            <PasswordInput
              required
              minLength={8}
              placeholder="password (min 8)"
              value={createForm.password}
              onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
              data-testid="create-user-password"
              eyeTestId="create-user-password-toggle"
              className="text-sm"
            />
            <select
              value={createForm.role}
              onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
              data-testid="create-user-role"
              className="bg-transparent border-b border-[var(--border)] focus:border-[var(--accent)] outline-none py-2 text-sm font-mono-ui tracking-[0.18em] uppercase"
            >
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
            <button
              type="submit"
              disabled={createBusy}
              data-testid="create-user-submit"
              className="py-2 font-mono-ui text-[10px] tracking-[0.28em] uppercase border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] disabled:opacity-40 transition-colors"
            >
              {createBusy ? "Creating…" : "Create"}
            </button>
            {createMsg && (
              <p
                data-testid="create-user-msg"
                className="md:col-span-4 font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-muted)]"
              >
                {createMsg}
              </p>
            )}
          </form>
        </div>
      </Card>

      {detailsUser && (
        <UserDetailsDrawer
          u={detailsUser}
          currentUser={currentUser}
          onClose={() => setDetailsId(null)}
          usersBusyId={usersBusyId}
          setUsersBusyId={setUsersBusyId}
          refreshUsers={refreshUsers}
        />
      )}

      <Card
        title="Super admin · Full-access allowlist"
        testid="section-superadmin-allowlist"
        description="Grant any account full admin + all beta access at runtime without a code redeploy. Elevation applies on the target user's next authenticated request. Restricted to configured super-admin accounts."
      >
        <SuperAdminAllowlistSection />
      </Card>

      <Card
        title="API beta allowlist"
        testid="section-api-allowlist"
        description="Approved users can mint personal API keys and call HBM programmatically."
      >
        <AdminApiAllowlistSection />
      </Card>

      <Card
        title="Research invites"
        testid="section-research-invites"
        description="Invitees are auto-consented to training use on signup."
      >
        <ResearchInvitesSection />
      </Card>

      <Card
        title="Change my password"
        testid="section-change-password"
        description="Rotate the credential you use to sign into this dashboard."
      >
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (pwdBusy) return;
            setPwdBusy(true);
            setPwdMsg("");
            try {
              await adminApi.changeOwnPassword(pwdForm.current_password, pwdForm.new_password);
              setPwdMsg("Password changed.");
              setPwdForm({ current_password: "", new_password: "" });
            } catch (err) {
              setPwdMsg(err.message || "Failed.");
            } finally {
              setPwdBusy(false);
            }
          }}
          className="grid md:grid-cols-3 gap-3 items-end max-w-2xl"
        >
          <PasswordInput
            required
            placeholder="current password"
            value={pwdForm.current_password}
            onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })}
            data-testid="own-current-password"
            eyeTestId="own-current-password-toggle"
            className="text-sm"
          />
          <PasswordInput
            required
            minLength={8}
            placeholder="new password (min 8)"
            value={pwdForm.new_password}
            onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })}
            data-testid="own-new-password"
            eyeTestId="own-new-password-toggle"
            className="text-sm"
          />
          <button
            type="submit"
            disabled={pwdBusy}
            data-testid="own-password-submit"
            className="py-2 font-mono-ui text-[10px] tracking-[0.28em] uppercase border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] disabled:opacity-40 transition-colors"
          >
            {pwdBusy ? "Saving…" : "Change password"}
          </button>
          {pwdMsg && (
            <p
              data-testid="own-password-msg"
              className="md:col-span-3 font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-muted)]"
            >
              {pwdMsg}
            </p>
          )}
        </form>
      </Card>
    </>
  );
}

// ─── User details drawer ───────────────────────────────────────────────
// Full-screen modal replacing the old per-row button spam. Every
// per-user control (role, unlimited-access allowlist, memory / voice
// betas, force-logout, delete/restore, reset password) lives here so
// the list stays scannable.

function UserDetailsDrawer({ u, currentUser, onClose, usersBusyId, setUsersBusyId, refreshUsers }) {
  const isSelf = currentUser && u.id === currentUser.id;
  const busy = usersBusyId === u.id;
  const limits = u.limits || {};
  const unlimited = !!limits.unlimited;
  const isDeleted = !!u.deleted_at;

  const run = async (label, fn, { confirmMsg, successMsg, refresh = true } = {}) => {
    if (confirmMsg && !confirm(confirmMsg)) return;
    setUsersBusyId(u.id);
    try {
      await fn();
      if (successMsg) alert(successMsg);
      if (refresh) await refreshUsers();
    } catch (e) {
      alert(e.message || `${label} failed.`);
    } finally {
      setUsersBusyId(null);
    }
  };

  const setUnlimited = (next) =>
    run(
      "Set unlimited",
      () => adminApi.setUserLimits(u.id, { unlimited: next }),
      { confirmMsg: next
          ? `Grant ${u.email} UNLIMITED access? Bypasses chat message and monthly-token caps without granting admin. Voice remains separate.`
          : `Revoke unlimited access for ${u.email}? Reverts to default caps.` },
    );

  const applyQuotaPreset = (preset) =>
    run(
      `Apply ${preset.label} tier`,
      () => adminApi.setUserLimits(u.id, {
        tier: preset.id,
        monthly_token_cap: preset.monthly_token_cap,
        daily_message_cap: preset.daily_message_cap,
        monthly_message_cap: preset.monthly_message_cap,
      }),
      {
        confirmMsg: `Set ${u.email} to ${preset.label}/month? This allows ${preset.monthly_token_cap.toLocaleString()} tokens/month, ${preset.daily_message_cap.toLocaleString()} messages/day, and ${preset.monthly_message_cap.toLocaleString()} messages/month.`,
      },
    );

  const clearQuotaPreset = () =>
    run(
      "Clear quota tier",
      () => adminApi.setUserLimits(u.id, {
        tier: null,
        monthly_token_cap: null,
        daily_message_cap: null,
        monthly_message_cap: null,
      }),
      { confirmMsg: `Clear token/message overrides for ${u.email}? Defaults remain unchanged.` },
    );

  const rows = [
    ["Email", u.email],
    ["User ID", u.id],
    ["Name", u.name || "—"],
    ["Role", u.role],
    ["Tier", u.tier || "free"],
    ["Unlimited", unlimited ? "yes" : "no"],
    ["Memory", u.memory_enabled ? "on" : "off"],
    ["Voice beta", u.role === "admin" ? "on (admin)" : (u.voice_beta ? "on" : "off")],
    ["Direct voice", u.role === "admin" ? "on (admin)" : (u.direct_voice_beta ? "on" : "off")],
    ["Created", u.created_at ? u.created_at.slice(0, 10) : "—"],
    ["Last active", u.last_active ? u.last_active.slice(0, 10) : "—"],
    ["Chats", typeof u.chat_count === "number" ? String(u.chat_count) : "—"],
    ["Daily messages", fmtLimit(limits.daily_message_cap)],
    ["Monthly messages", fmtLimit(limits.monthly_message_cap)],
    ["Monthly tokens", fmtLimit(limits.monthly_token_cap)],
    ["Deleted", isDeleted ? u.deleted_at.slice(0, 10) : "no"],
  ];

  const btn =
    "font-mono-ui text-[10px] tracking-[0.24em] uppercase px-3 py-2 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-40 transition-colors text-left";
  const dangerBtn =
    "font-mono-ui text-[10px] tracking-[0.24em] uppercase px-3 py-2 border border-[var(--border)] hover:border-red-500 hover:text-red-400 disabled:opacity-40 transition-colors text-left";
  const goodBtn =
    "font-mono-ui text-[10px] tracking-[0.24em] uppercase px-3 py-2 border border-emerald-500/50 text-emerald-400 hover:border-emerald-400 hover:bg-emerald-500/10 disabled:opacity-40 transition-colors text-left";

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid={`user-details-drawer-${u.id}`}
      className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-[var(--bg)] border-l border-[var(--border)] h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 md:px-8 py-6 border-b border-[var(--border)] flex items-start justify-between gap-4 sticky top-0 bg-[var(--bg)] z-10">
          <div className="min-w-0">
            <h4 className="font-mono-ui text-[11px] tracking-[0.34em] uppercase text-[var(--text)]">
              User details
            </h4>
            <p className="font-ui text-[var(--text)] mt-2 break-all">{u.email}</p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {u.role === "admin" && (
                <span className="font-mono-ui text-[9px] tracking-[0.2em] uppercase border border-[var(--accent)] text-[var(--accent)] px-1.5 py-0.5">admin</span>
              )}
              {unlimited && (
                <span className="font-mono-ui text-[9px] tracking-[0.2em] uppercase border border-emerald-500/60 text-emerald-400 px-1.5 py-0.5">unlimited</span>
              )}
              {isDeleted && (
                <span className="font-mono-ui text-[9px] tracking-[0.2em] uppercase border border-red-500/60 text-red-400 px-1.5 py-0.5">deleted</span>
              )}
              {isSelf && (
                <span className="font-mono-ui text-[9px] tracking-[0.2em] uppercase border border-[var(--border)] text-[var(--text-dim)] px-1.5 py-0.5">you</span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="user-details-close"
            className="font-mono-ui text-[10px] tracking-[0.28em] uppercase px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors shrink-0"
          >
            Close
          </button>
        </div>

        <div className="px-6 md:px-8 py-6 space-y-6">
          <section>
            <p className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text-muted)] mb-3">
              Profile
            </p>
            <dl className="grid grid-cols-1 sm:grid-cols-2 border border-[var(--border)]">
              {rows.map(([k, v]) => (
                <div key={k} className="border-b sm:border-b border-[var(--border)] px-3 py-2 sm:[&:nth-child(odd)]:border-r">
                  <dt className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">{k}</dt>
                  <dd className="font-ui text-sm text-[var(--text)] mt-1 break-all">{v}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section>
            <p className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text-muted)] mb-3">
              Access
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                disabled={busy || isSelf}
                onClick={() =>
                  run(
                    "Set role",
                    () => adminApi.setUserRole(u.id, u.role === "admin" ? "user" : "admin"),
                    { confirmMsg: `Set ${u.email} role to ${u.role === "admin" ? "user" : "admin"}?` },
                  )
                }
                data-testid={`user-role-toggle-${u.id}`}
                className={btn}
              >
                {u.role === "admin" ? "Demote from admin" : "Make admin"}
              </button>

              <button
                disabled={busy || u.role === "admin"}
                onClick={() => setUnlimited(!unlimited)}
                title={
                  u.role === "admin"
                    ? "Admins already have unlimited usage."
                    : "Grant unlimited usage (bypasses chat message and monthly-token caps; voice remains separate) without granting admin."
                }
                data-testid={`user-toggle-unlimited-${u.id}`}
                className={unlimited ? goodBtn : btn}
              >
                {u.role === "admin"
                  ? "Unlimited: on (admin)"
                  : unlimited
                  ? "Unlimited: on · revoke"
                  : "Grant unlimited access"}
              </button>

              {TOKEN_LIMIT_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  disabled={busy || unlimited || u.role === "admin"}
                  onClick={() => applyQuotaPreset(preset)}
                  data-testid={`user-limit-preset-${preset.id}-${u.id}`}
                  className={limits.tier === preset.id ? goodBtn : btn}
                  title={`${preset.monthly_token_cap.toLocaleString()} tokens/month · ${preset.daily_message_cap.toLocaleString()} messages/day · ${preset.monthly_message_cap.toLocaleString()} messages/month`}
                >
                  Set {preset.label} / month
                </button>
              ))}

              <button
                disabled={busy || unlimited || u.role === "admin"}
                onClick={clearQuotaPreset}
                data-testid={`user-limit-clear-${u.id}`}
                className={btn}
              >
                Clear token tier
              </button>

              <button
                disabled={busy}
                onClick={() =>
                  run(
                    "Reset password",
                    async () => {
                      const np = prompt(`New password for ${u.email}? (min 8 chars)`);
                      if (!np || np.length < 8) throw new Error("cancelled");
                      await adminApi.resetUserPassword(u.id, np);
                    },
                    { successMsg: "Password reset.", refresh: false },
                  )
                }
                data-testid={`user-reset-pwd-${u.id}`}
                className={btn}
              >
                Reset password
              </button>

              <button
                disabled={busy || isSelf}
                onClick={() =>
                  run(
                    "Force logout",
                    () => adminApi.forceLogoutUser(u.id),
                    {
                      confirmMsg: `Force-logout ${u.email}? All their sessions will be invalidated.`,
                      successMsg: "All sessions invalidated.",
                    },
                  )
                }
                data-testid={`user-force-logout-${u.id}`}
                className={btn}
              >
                Force logout
              </button>

              <button
                disabled={busy}
                onClick={() => run("Toggle memory", () => adminApi.toggleUserMemory(u.id))}
                data-testid={`user-toggle-memory-${u.id}`}
                className={btn}
              >
                Memory: {u.memory_enabled ? "on · turn off" : "off · turn on"}
              </button>

              <button
                disabled={busy || u.role === "admin"}
                onClick={() => run("Toggle voice", () => adminApi.setUserVoiceBeta(u.id, !u.voice_beta))}
                title={u.role === "admin" ? "Admins already have voice access." : "Grant this user access to Voice Mode."}
                data-testid={`user-toggle-voice-${u.id}`}
                className={btn}
              >
                Voice beta: {u.role === "admin" ? "on (admin)" : u.voice_beta ? "on · revoke" : "off · grant"}
              </button>

              <button
                disabled={busy || u.role === "admin"}
                onClick={() =>
                  run("Toggle direct voice", () => adminApi.setUserDirectVoiceBeta(u.id, !u.direct_voice_beta))
                }
                title={
                  u.role === "admin"
                    ? "Admins already have Direct Voice access."
                    : "Grant access to OpenAI Realtime (Direct Voice). Requires voice_beta too."
                }
                data-testid={`user-toggle-direct-voice-${u.id}`}
                className={btn}
              >
                Direct voice: {u.role === "admin" ? "on (admin)" : u.direct_voice_beta ? "on · revoke" : "off · grant"}
              </button>

              {isDeleted ? (
                <button
                  disabled={busy}
                  onClick={() =>
                    run("Restore", () => adminApi.restoreUser(u.id), { successMsg: "Restored." })
                  }
                  data-testid={`user-restore-${u.id}`}
                  className={goodBtn}
                >
                  Restore account
                </button>
              ) : (
                <button
                  disabled={busy || isSelf}
                  onClick={() =>
                    run(
                      "Soft delete",
                      () => adminApi.softDeleteUser(u.id),
                      {
                        confirmMsg: `Soft-delete ${u.email}? Their account will be disabled but audit history is kept.`,
                      },
                    )
                  }
                  data-testid={`user-soft-delete-${u.id}`}
                  className={dangerBtn}
                >
                  Delete account
                </button>
              )}
            </div>
          </section>

          <p className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">
            For finer per-user cap editing (custom daily/monthly caps, tier), use the Usage Analytics tab → user lookup.
          </p>
        </div>
      </div>
    </div>
  );
}

